import React from 'react';
import { PageSection, ServicesSectionContent } from '../../types/page';
import { getIcon } from '../../lib/iconMapper';
import { Button } from '../ui/button';
import { ArrowRight } from 'lucide-react';

interface ServicesSectionProps {
  section: PageSection;
  className?: string;
}

export const ServicesSection: React.FC<ServicesSectionProps> = ({ 
  section, 
  className = '' 
}) => {
  const content = section.content as ServicesSectionContent;
  
  if (!content.items || content.items.length === 0) {
    return null;
  }

  const backgroundStyle = content.backgroundImage 
    ? { backgroundImage: `url(${content.backgroundImage})` }
    : {};

  return (
    <section 
      className={`relative py-20 bg-cover bg-center bg-no-repeat ${className}`}
      style={backgroundStyle}
    >
      {/* Overlay */}
      {content.backgroundImage && (
        <div className="absolute inset-0 bg-black/70" />
      )}
      
      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
            content.backgroundImage ? 'text-white' : 'text-gray-900'
          }`}>
            {section.title || 'Nuestros Servicios'}
          </h2>
          {section.subtitle && (
            <p className={`text-xl max-w-2xl mx-auto ${
              content.backgroundImage ? 'text-gray-200' : 'text-gray-600'
            }`}>
              {section.subtitle}
            </p>
          )}
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {content.items.map((service, index) => {
            const IconComponent = getIcon(service.icon);
            
            return (
              <div 
                key={index}
                className={`text-center p-6 rounded-xl transition-transform hover:scale-105 ${
                  content.backgroundImage 
                    ? 'bg-white/10 backdrop-blur-sm border border-white/20' 
                    : 'bg-white shadow-lg border border-gray-100 hover:shadow-xl'
                }`}
              >
                {IconComponent && (
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 ${
                    content.backgroundImage
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    <IconComponent className="w-8 h-8" />
                  </div>
                )}
                
                <h3 className={`text-xl font-bold mb-4 ${
                  content.backgroundImage ? 'text-white' : 'text-gray-900'
                }`}>
                  {service.title}
                </h3>
                
                <p className={`text-base mb-6 leading-relaxed ${
                  content.backgroundImage ? 'text-gray-200' : 'text-gray-600'
                }`}>
                  {service.description}
                </p>
                
                {service.link && (
                  <Button 
                    variant={content.backgroundImage ? "outline" : "default"}
                    size="sm"
                    className={content.backgroundImage ? 
                      "border-white text-white hover:bg-white hover:text-gray-900" : 
                      ""
                    }
                    onClick={() => window.location.href = service.link!}
                  >
                    Más información
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};